import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import type { ApiClient, Tuerfeld, Tuertyp } from '../api';
import { IconTuertyp, IconPlus, GruppenIcon } from '../Icons';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ContentSection, DataTable, EmptyState, ErrorState, Feld, FormSection,
  LoadingState, formatiereZahl, leseZahl, type Spalte,
} from '../muster';

/**
 * Verwaltung der Türtypen.
 *
 * Im Modell steht unter TuV_E_Tuer_Typ nur ein Schlüssel wie „Typ04". Was
 * dahinter an Ausstattung hängt, beschreibt die Vorlage hier - vierzig
 * Brandschutztüren mit denselben Kontakten einmal statt vierzig Mal.
 *
 * Hausweite Vorlagen gelten für alle Projekte. Eine projektbezogene überschreibt
 * sie, falls ein Bauherr eigene Vorgaben hat.
 */
export function Tuertypen({ api }: { api: ApiClient }) {
  const { id: projektId } = useParams<{ id: string }>();

  const [typen, setTypen] = useState<Tuertyp[] | null>(null);
  const [felder, setFelder] = useState<Tuerfeld[] | null>(null);
  const [gewaehlt, setGewaehlt] = useState<string | null>(null);
  const [anlegen, setAnlegen] = useState(false);
  const [fehler, setFehler] = useState<unknown>(null);

  const laden = useCallback(async () => {
    try {
      const [t, f] = await Promise.all([api.tuertypen(projektId), api.tuerfelder()]);
      setTypen(t);
      setFelder(f);
      setFehler(null);
      setGewaehlt((bisher) => bisher ?? (t.length > 0 ? t[0].id : null));
    } catch (e) {
      setFehler(e);
    }
  }, [api, projektId]);

  useEffect(() => { laden(); }, [laden]);

  const kopf = (
    <PageHeader
      titel="Türtypen"
      beschreibung={'Im Modell steht unter TuV_E_Tuer_Typ nur ein Schlüssel wie '
        + '„Typ04". Was dahinter an Ausstattung hängt, beschreibt die Vorlage hier — '
        + 'vierzig Brandschutztüren mit denselben Kontakten einmal statt vierzig Mal.'}
      meta={typen && <>{typen.length} Typen</>}
      aktionen={(
        <button className="m-knopf m-knopf-haupt" onClick={() => setAnlegen(!anlegen)}>
          {anlegen ? 'Abbrechen' : <><IconPlus size={16} />Typ anlegen</>}
        </button>
      )}
    />
  );

  if (fehler && (!typen || !felder)) {
    return (
      <>
        <p><Link to={`/projekte/${projektId}/tuerbuch`}>← Zum Türbuch</Link></p>
        {kopf}
        <ErrorState
          meldung="Die Türtypen konnten nicht geladen werden."
          technisch={fehler}
          beiErneutVersuchen={laden}
        />
      </>
    );
  }

  if (!typen || !felder) {
    return (
      <>
        <p><Link to={`/projekte/${projektId}/tuerbuch`}>← Zum Türbuch</Link></p>
        {kopf}
        <LoadingState text="Türtypen werden geladen …" zeilen={6} spalten={2} />
      </>
    );
  }

  const typ = typen.find((t) => t.id === gewaehlt) ?? null;

  return (
    <>
      <p><Link to={`/projekte/${projektId}/tuerbuch`}>← Zum Türbuch</Link></p>
      {kopf}

      {anlegen && (
        <Anlegeformular
          api={api}
          projektId={projektId}
          fertig={() => { setAnlegen(false); laden(); }}
        />
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 18,
                    alignItems: 'start' }}>
        <div className="karte" style={{ padding: 0, position: 'sticky', top: 16 }}>
          <h2 className="karte-titel" style={{ padding: '14px 14px 8px', margin: 0 }}>
            <IconTuertyp />Türtypen
          </h2>

          <div style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto',
                        borderTop: '1px solid var(--linie)' }}>
            {typen.map((t) => {
              const werte = t.dataJson ? Object.keys(JSON.parse(t.dataJson)).length : 0;
              const anforderungen = t.anforderungen?.length ?? 0;

              return (
                <button
                  key={t.id}
                  onClick={() => setGewaehlt(t.id)}
                  style={{
                    width: '100%', textAlign: 'left', border: 'none',
                    borderBottom: '1px solid var(--linie)', borderRadius: 0,
                    padding: '9px 14px', fontSize: 13,
                    background: gewaehlt === t.id ? 'var(--flaeche)' : 'transparent',
                    opacity: t.isActive === false ? 0.5 : 1,
                  }}
                >
                  <strong>{t.templateKey}</strong>
                  <br />
                  <span className="sehr-schwach">{t.name}</span>
                  <br />
                  <span className="sehr-schwach" style={{ fontSize: 11 }}>
                    {werte === 0
                      ? 'keine Werte'
                      : `${werte} ${werte === 1 ? 'Wert' : 'Werte'}`}
                    {anforderungen > 0 && ` · ${anforderungen} Elt-Anforderungen`}
                    {t.projectId ? ' · nur dieses Projekt' : ' · hausweit'}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {typ
          ? <Typdetail key={typ.id} api={api} typ={typ} felder={felder} neuLaden={laden} />
          : (
            <EmptyState
              icon={<IconTuertyp />}
              titel={typen.length === 0 ? 'Noch kein Türtyp angelegt.' : 'Kein Typ gewählt.'}
              erklaerung={typen.length === 0
                ? 'Ein Türtyp bündelt, was an allen Türen mit demselben Schlüssel '
                  + 'gleich ist — Ausstattung, Anforderungen, Kennwerte.'
                : 'Links einen Typ wählen; seine Werte erscheinen dann hier.'}
              aktion={typen.length === 0 ? (
                <button className="m-knopf m-knopf-haupt" onClick={() => setAnlegen(true)}>
                  <IconPlus size={16} />Typ anlegen
                </button>
              ) : undefined}
            />
          )}
      </div>
    </>
  );
}

// --- Detail -------------------------------------------------------------------

function Typdetail({
  api, typ, felder, neuLaden,
}: {
  api: ApiClient;
  typ: Tuertyp;
  felder: Tuerfeld[];
  neuLaden: () => void;
}) {
  const ausDb: Record<string, string | null> = typ.dataJson ? JSON.parse(typ.dataJson) : {};

  const [name, setName] = useState(typ.name);
  const [beschreibung, setBeschreibung] = useState(typ.beschreibung ?? '');
  const [werte, setWerte] = useState(ausDb);
  const [gespeichert, setGespeichert] = useState(false);
  const [fehler, setFehler] = useState<unknown>(null);
  const [laeuft, setLaeuft] = useState(false);

  const geaendert = useMemo(() => {
    if (name !== typ.name) return true;
    if (beschreibung !== (typ.beschreibung ?? '')) return true;

    const alle = new Set([...Object.keys(ausDb), ...Object.keys(werte)]);
    for (const k of alle) {
      if ((ausDb[k] ?? '') !== (werte[k] ?? '')) return true;
    }
    return false;
  }, [name, beschreibung, ausDb, werte, typ]);

  // Nur Felder, die ein Typ sinnvoll vorgeben kann. Die Kennungsfelder gehören
  // zur einzelnen Tür - eine Türnummer als Vorlage wäre unsinnig.
  const gruppen = useMemo(() => {
    const map = new Map<string, Tuerfeld[]>();

    for (const f of felder) {
      if (f.gruppe === 'Kennung') continue;

      const g = f.gruppe ?? 'Sonstige';
      if (!map.has(g)) map.set(g, []);
      map.get(g)!.push(f);
    }

    return [...map.entries()];
  }, [felder]);

  const anforderungsSpalten: Spalte<NonNullable<Tuertyp['anforderungen']>[number]>[] =
    useMemo(() => [
      { schluessel: 'kategorie', titel: 'Kategorie', wert: (a) => a.kategorie },
      {
        schluessel: 'komponente', titel: 'Komponente', immerSichtbar: true,
        wert: (a) => a.komponente,
      },
      {
        schluessel: 'menge', titel: 'Menge', art: 'zahl', breite: '90px',
        wert: (a) => a.menge,
      },
      { schluessel: 'pflicht', titel: 'Pflicht', wert: (a) => a.pflichtstatus },
      { schluessel: 'system', titel: 'System', wert: (a) => a.system },
      {
        schluessel: 'bemerkung', titel: 'Bemerkung',
        wert: (a) => a.bemerkung ?? '',
        inhalt: (a) => a.bemerkung ?? '–',
      },
    ], []);

  const speichern = async () => {
    setLaeuft(true);
    setFehler(null);
    try {
      await api.tuertypAendern(typ.id, {
        name: name.trim(),
        beschreibung: beschreibung.trim() || null,
        werte,
      });
      setGespeichert(true);
      setTimeout(() => setGespeichert(false), 2500);
      neuLaden();
    } catch (e) {
      setFehler(e);
    } finally {
      setLaeuft(false);
    }
  };

  const gesetzt = Object.values(werte).filter((v) => v && v.trim()).length;

  return (
    <div>
      <div className="karte" style={{ position: 'sticky', top: 16, zIndex: 2 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <h1 style={{ fontSize: 20, fontWeight: 300, margin: 0 }}>{typ.templateKey}</h1>
          <span className="sehr-schwach">
            {gesetzt === 0 ? 'noch keine Werte' : `${gesetzt} Werte gesetzt`}
          </span>

          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
            {gespeichert && <span style={{ color: 'var(--gut)', fontSize: 13 }}>gespeichert</span>}
            <button className="m-knopf m-knopf-haupt" onClick={speichern}
                    disabled={!geaendert || laeuft}>
              {laeuft ? 'einen Moment …' : 'Speichern'}
            </button>
          </div>
        </div>

        <div className="m-feldgitter m-feldgitter-2" style={{ marginTop: 14, maxWidth: 640 }}>
          <Feld
            beschriftung="Bezeichnung"
            wert={name}
            beiAenderung={(w) => setName(w as string)}
          />
          <Feld
            beschriftung="Beschreibung"
            wert={beschreibung}
            beiAenderung={(w) => setBeschreibung(w as string)}
            platzhalter="wofür dieser Typ gedacht ist"
          />
        </div>

        {fehler !== null && (
          <ErrorState klein meldung="Der Türtyp konnte nicht gespeichert werden."
                      technisch={fehler} />
        )}

        <p className="sehr-schwach" style={{ marginBottom: 0, marginTop: 12, maxWidth: 640 }}>
          Was hier steht, wird beim Anwenden auf die Türen dieses Typs übertragen.
          Leere Felder bleiben unberührt - eine Vorlage muss nicht alles vorgeben.
        </p>
      </div>

      {(typ.anforderungen?.length ?? 0) > 0 && (
        <DataTable
          name="Elektrische Anforderungen"
          beschreibung={'Strukturierter Planinhalt. Änderungen erfolgen über eine neue '
            + 'Quellenrevision; die bisherigen Anforderungen bleiben erhalten.'}
          spalten={anforderungsSpalten}
          zeilen={typ.anforderungen!}
          schluessel={(a) => a.id}
          suche={typ.anforderungen!.length > 8}
          ausfuhr
          dicht
        />
      )}

      {(typ.anforderungen?.length ?? 0) > 0 && (
        <p className="sehr-schwach">
          Quelle: {typ.anforderungen![0].quelldokument} · Planstand{' '}
          {typ.anforderungen![0].planstand} vom{' '}
          {new Date(`${typ.anforderungen![0].quelldatum}T00:00:00`)
            .toLocaleDateString('de-DE')}
        </p>
      )}

      {gruppen.map(([gruppe, gruppenfelder]) => (
        <ContentSection
          key={gruppe}
          titel={gruppe}
          werkzeuge={<GruppenIcon gruppe={gruppe} />}
          aufklappbar
        >
          <div className="m-feldgitter m-feldgitter-3">
            {gruppenfelder.map((f) => (
              <Vorlagenfeld
                key={f.id}
                feld={f}
                wert={werte[f.fieldKey] ?? ''}
                setzen={(v) => setWerte({ ...werte, [f.fieldKey]: v || null })}
              />
            ))}
          </div>
        </ContentSection>
      ))}
    </div>
  );
}

function Vorlagenfeld({
  feld, wert, setzen,
}: {
  feld: Tuerfeld;
  wert: string;
  setzen: (v: string) => void;
}) {
  if (feld.dataType === 'ja_nein') {
    return (
      <Feld
        art="auswahl"
        beschriftung={feld.label}
        wert={wert}
        beiAenderung={(w) => setzen(w as string)}
        optionen={[
          { wert: '', beschriftung: 'nicht vorgeben' },
          { wert: 'TRUE', beschriftung: 'ja' },
          { wert: 'FALSE', beschriftung: 'nein' },
        ]}
      />
    );
  }

  /* Abschnitt 15: Wert und Einheit getrennt. Gespeichert wird weiterhin ein
     Text - `leseZahl`/`formatiereZahl` uebersetzen an der Grenze, damit ein
     aus einer Tabelle kopiertes „36.0" nicht zu 360 wird. */
  if (feld.dataType === 'zahl') {
    return (
      <Feld
        art="zahl"
        beschriftung={feld.label}
        einheit={feld.unit ?? undefined}
        wert={leseZahl(wert)}
        beiAenderung={(w) => setzen(w === null ? '' : formatiereZahl(w as number))}
      />
    );
  }

  return (
    <Feld
      beschriftung={feld.unit ? `${feld.label} [${feld.unit}]` : feld.label}
      wert={wert}
      beiAenderung={(w) => setzen(w as string)}
      platzhalter="—"
    />
  );
}

// --- Anlegen ------------------------------------------------------------------

function Anlegeformular({
  api, projektId, fertig,
}: {
  api: ApiClient;
  projektId?: string;
  fertig: () => void;
}) {
  const [schluessel, setSchluessel] = useState('');
  const [name, setName] = useState('');
  const [beschreibung, setBeschreibung] = useState('');
  const [nurProjekt, setNurProjekt] = useState(false);
  const [fehler, setFehler] = useState<unknown>(null);
  const [laeuft, setLaeuft] = useState(false);

  const speichern = async () => {
    setLaeuft(true);
    setFehler(null);
    try {
      await api.tuertypAnlegen({
        templateKey: schluessel.trim(),
        name: name.trim(),
        beschreibung: beschreibung.trim() || null,
        projectId: nurProjekt ? projektId : null,
      });
      fertig();
    } catch (e) {
      setFehler(e);
      setLaeuft(false);
    }
  };

  return (
    <FormSection
      titel="Neuer Türtyp"
      beschreibung={'Ohne den Haken „nur für dieses Projekt" gilt der Typ hausweit '
        + 'und steht in allen Projekten zur Verfügung. Das ist der Regelfall — '
        + 'Türtypen wiederholen sich.'}
      spalten={2}
      fuss={(
        <button
          className="m-knopf m-knopf-haupt"
          onClick={speichern}
          disabled={!schluessel.trim() || !name.trim() || laeuft}
        >
          {laeuft ? 'wird angelegt …' : 'Anlegen'}
        </button>
      )}
    >
      {fehler !== null && (
        <ErrorState klein meldung="Der Türtyp konnte nicht angelegt werden."
                    technisch={fehler} />
      )}

      <Feld
        beschriftung="Schlüssel"
        wert={schluessel}
        beiAenderung={(w) => setSchluessel(w as string)}
        platzhalter="Typ10"
        pflicht
        hinweis="Genau der Wert, der im Modell unter TuV_E_Tuer_Typ steht."
      />
      <Feld
        beschriftung="Bezeichnung"
        wert={name}
        beiAenderung={(w) => setName(w as string)}
        platzhalter="Typ 10 - Fluchttür mit Terminal"
        pflicht
      />
      <Feld
        beschriftung="Beschreibung"
        wert={beschreibung}
        beiAenderung={(w) => setBeschreibung(w as string)}
      />
      <Feld
        art="haken"
        beschriftung="nur für dieses Projekt"
        wert={nurProjekt}
        beiAenderung={(w) => setNurProjekt(w as boolean)}
      />
    </FormSection>
  );
}
